import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import { FORM } from 'src/constants/Form';
import { SEARCH_LOGICAL_OPERATORS } from 'src/models/AdvanceSearchModel';
import { getEntitiesAction, setFilters } from 'src/redux/actions/querybuilderActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { useAppDispatch } from 'src/redux/Store';
import { useCallback, useEffect, useState } from 'react';
import { getPaymentFilter, getPaymentRangeDate } from 'src/lib/PaymentHelper';
import { PaymentSections } from 'src/components/Pages/UserPaymentsPage';
import { debounce } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
// eslint-disable-next-line import/no-extraneous-dependencies
import MomentUtils from '@date-io/moment';

interface Props {
  section: PaymentSections,
}

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    gap: '8px',
    alignItems: 'center',
  },
  col2: {
    flex: 1,
    flexDirection: 'column',
    width: '50%',
    alignItems: 'center',
  },
}));

const UserPaymentSearch = (props: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const { section } = props;

  const form = FORM.payment_search;

  const [start, end] = getPaymentRangeDate(section);

  const [terms, setTerms] = useState('');

  const [startDate, setStartDate] = useState(start);

  const [endDate, setEndDate] = useState(end);

  useEffect(() => {
    const [newStartDate, newEndDate] = getPaymentRangeDate(section);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, [section]);

  const handleSearch = () => {
    const filters = getPaymentFilter(
      startDate,
      endDate,
      terms,
    );
    dispatch(setFilters({
      entity: ENTITIES.payments,
      filters: { [SEARCH_LOGICAL_OPERATORS.and]: filters },
    }));
    dispatch(getEntitiesAction(ENTITIES.payments));
  };

  const searchInvoice = useCallback(
    debounce(async () => {
      handleSearch();
    }, 300),
    [], // will be created only once initially
  );

  const onChange = (value: any) => {
    setTerms(value);
    if (value.length > 2) {
      searchInvoice();
    }
  };

  const handleDateChange = (position: number, value: MaterialUiPickersDate) => {
    if (!value) { return; }

    let newStartDate;
    let newEndDate;

    if (position === 0) {
      newStartDate = value;
      newEndDate = endDate;
    } else {
      newStartDate = startDate;
      newEndDate = value;
    }
    if (newEndDate < newStartDate) {
      setStartDate(newStartDate);
      setEndDate(newStartDate);
      return;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    searchInvoice();
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div
        className={classes.row}
        style={{ maxWidth: '680px', alignSelf: 'center' }}>
        <FormTextField
          form={form}
          label="Payment # or title"
          name="term"
          onChange={onChange}
          value={terms}
          margin="dense" />
      </div>
      <div
        className={classes.row}>
        <div className={classes.col2}>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <b>Start:</b>
            <DatePicker
              format="MM/DD/yyyy"
              margin="dense"
              id="date-picker-inline-start"
              value={startDate}
              disableFuture={section === PaymentSections.error}
              disablePast={section === PaymentSections.scheduled}
              onChange={(value: MaterialUiPickersDate) => handleDateChange(0, value)} />
          </span>
        </div>
        <div className={classes.col2}>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <b>End:</b>
            <DatePicker
              format="MM/DD/yyyy"
              margin="dense"
              id="date-picker-inline-end"
              value={endDate}
              disablePast={section === PaymentSections.scheduled}
              disableFuture={section === PaymentSections.error}
              onChange={(value: MaterialUiPickersDate) => handleDateChange(1, value)} />
          </span>
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default UserPaymentSearch;
